// Shared knowledge-nav definition — the single source of truth for the
// knowledge sections, their grouping, and icons. Consumed by KnowledgePage.vue
// (slug validation + the current page's title) and Sidebar.vue (the main
// sidebar's knowledge mode), so the two surfaces can't drift apart. Mirrors
// the shape of config/settingsNav.ts, minus the plan gating (Knowledge has none).
import type { Component } from 'vue'
import i18n from '@/i18n'
import {
  PhArrowLeft,
  PhBriefcase,
  PhGauge,
  PhMapTrifold,
  PhSquaresFour,
  PhListChecks,
  PhStorefront,
  PhFlag,
  PhPackage,
  PhLightning,
  PhUsersThree,
  PhUsers,
  PhShieldWarning,
  PhChatCircle,
  PhChatCircleText,
  PhProhibit,
  PhTarget,
  PhFunnel,
  PhPalette,
  PhMagnet,
  PhUserCircle,
  PhBooks,
} from '@phosphor-icons/vue'

export interface KnowledgeNavItemDef {
  /** Slug — also the URL segment (/knowledge/:id). */
  id: string
  label: string
  icon: Component
  /** Prose pages hold a single rich-text body: their action button reads
   * "Edit" (not "Create") and the shell hides the search box for them. */
  prose?: boolean
  /** List pages that carry their own add-row and don't need the shell's
   * search box (short lists — Jens 2026-08-18, starting with Segments). */
  noSearch?: boolean
  /** Page heading when it should differ from the (shorter) menu label —
   * e.g. the nav says "Content" under STRATEGY, the page says
   * "Content Strategy". */
  pageTitle?: string
}

export interface KnowledgeNavGroupDef {
  label: string
  items: KnowledgeNavItemDef[]
}

/** Icon for the "back to the main menu" row in the sidebar's knowledge mode. */
export const KNOWLEDGE_BACK_ICON = PhArrowLeft

export const KNOWLEDGE_NAV_GROUPS: KnowledgeNavGroupDef[] = [
  {
    get label() { return i18n.global.t('knowledgeNav.group.overview') },
    items: [
      // The section's landing page (Jens 2026-08-17): completeness at a
      // glance + any question still waiting, answerable in place. prose
      // hides the search box and the create button — it isn't a list.
      // pageTitle '' suppresses the shell's <h1> — the progress card is this
      // page's headline.
      { id: 'overview', get label() { return i18n.global.t('knowledgeNav.item.overview') }, pageTitle: '', icon: PhGauge, prose: true },
      // The finished document the whole Brain builds toward — lives right
      // under Overview (Jens 2026-08-18), greyed until the context is
      // complete (the sidebar + page guard key on strategyReady).
      { id: 'strategy', get label() { return i18n.global.t('knowledgeNav.item.strategy') }, icon: PhMapTrifold, prose: true },
      { id: 'research', get label() { return i18n.global.t('knowledge.research.title') }, icon: PhBooks },
    ],
  },
  {
    get label() { return i18n.global.t('knowledgeNav.group.foundation') },
    items: [
      // The business brief — what every other section is generated from.
      { id: 'your-business', get label() { return i18n.global.t('knowledgeNav.item.yourBusiness') }, icon: PhBriefcase, prose: true },
      // Added 2026-08-18 (Jens): every overview row needs a page behind it.
      { id: 'goals', get label() { return i18n.global.t('knowledgeNav.item.goals') }, icon: PhTarget, prose: true },
      // One choice, not a list — prose hides the search box + create button.
      { id: 'how-people-buy', get label() { return i18n.global.t('knowledgeNav.item.howPeopleBuy') }, icon: PhFunnel, prose: true },
    ],
  },
  // Group order mirrors the Overview page (config/knowledgeSections.ts) —
  // one order everywhere (Jens 2026-08-18). The further down, the more
  // derived the content; the finished Strategy document comes last.
  {
    get label() { return i18n.global.t('settings.ai.entity.audience') },
    items: [
      { id: 'audience', get label() { return i18n.global.t('settings.ai.entity.audience') }, icon: PhUsersThree, prose: true },
      { id: 'segments', get label() { return i18n.global.t('knowledgeNav.item.segments') }, icon: PhUsers, noSearch: true },
      { id: 'objections', get label() { return i18n.global.t('knowledgeNav.item.objections') }, icon: PhShieldWarning, noSearch: true },
    ],
  },
  {
    get label() { return i18n.global.t('settings.ai.entity.offer') },
    items: [
      { id: 'offer', get label() { return i18n.global.t('settings.ai.entity.offer') }, icon: PhStorefront, prose: true },
      { id: 'positioning', get label() { return i18n.global.t('settings.ai.entity.positioning') }, icon: PhFlag, prose: true },
      { id: 'value-propositions', get label() { return i18n.global.t('knowledgeNav.item.valuePropositions') }, icon: PhPackage, noSearch: true },
      { id: 'differentiators', get label() { return i18n.global.t('knowledgeNav.item.differentiators') }, icon: PhLightning, noSearch: true },
    ],
  },
  {
    get label() { return i18n.global.t('knowledgeNav.group.voiceAndRules') },
    items: [
      { id: 'brand-voice', get label() { return i18n.global.t('settings.ai.entity.brandVoice') }, icon: PhChatCircle, prose: true },
      // Optional manual library: it is intentionally outside Brain
      // completeness and section locking.
      { id: 'voice-profiles', get label() { return i18n.global.t('knowledgeNav.item.voiceProfiles') }, icon: PhUserCircle, noSearch: true },
      { id: 'brand-look', get label() { return i18n.global.t('knowledgeNav.item.brandLook') }, icon: PhPalette, prose: true },
      { id: 'customer-language', get label() { return i18n.global.t('knowledgeNav.item.customerLanguage') }, icon: PhChatCircleText, noSearch: true },
      { id: 'hard-rules', get label() { return i18n.global.t('knowledgeNav.item.hardRules') }, icon: PhProhibit, noSearch: true },
    ],
  },
  {
    get label() { return i18n.global.t('knowledgeNav.group.content') },
    items: [
      { id: 'pillars', get label() { return i18n.global.t('knowledgeNav.item.pillars') }, icon: PhSquaresFour, noSearch: true },
      { id: 'roster', get label() { return i18n.global.t('knowledgeNav.item.roster') }, icon: PhListChecks },
      { id: 'lead-magnet-ideas', get label() { return i18n.global.t('knowledgeNav.item.teasers') }, icon: PhMagnet, noSearch: true },
    ],
  },
]

/** Flat list of every slug, in nav order. */
export const KNOWLEDGE_SLUGS: string[] = KNOWLEDGE_NAV_GROUPS.flatMap(g => g.items.map(i => i.id))

/** Is this a known knowledge slug? */
export function isKnowledgeSlug(id: string): boolean {
  return KNOWLEDGE_SLUGS.includes(id)
}

/** Flat item lookup by slug. */
export function findKnowledgeItem(id: string): KnowledgeNavItemDef | null {
  for (const group of KNOWLEDGE_NAV_GROUPS) {
    const item = group.items.find(i => i.id === id)
    if (item) return item
  }
  return null
}

/** Workspace-relative route for a knowledge item — the sidebar prefixes the
 * workspace, exactly like settingsItemRoute. */
export function knowledgeItemRoute(id: string): string {
  return `/brain/${id}`
}
